import { useMemo } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import PageHeader from "@/components/feature/PageHeader";
import FaqAccordion from "@/components/feature/FaqAccordion";
import { useSeo } from "@/hooks/useSeo";
import { faqs } from "@/mocks/faqs";
import { categories } from "@/mocks/categories";
import { siteInfo } from "@/mocks/site";

export default function FaqPage() {
  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        inLanguage: "zh-CN",
        mainEntity: faqs.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "常见问题", item: "/faq" },
        ],
      },
    ],
    [],
  );

  useSeo({
    title: "常见问题 - 黑料官方公告引用与线索提交说明",
    description:
      "关于公告引用、线索提交、公告更新与防诈骗的常见问题解答。了解黑料官方如何处理核实请求、如何识别官方客服，以及遇到疑似不实信息该怎么做。",
    keywords: "黑料官方常见问题,公告引用,线索提交,识别官方客服,公告更新",
    path: "/faq",
    jsonLd,
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <PageHeader
          eyebrow="使用与核实说明"
          title="常见问题"
          description="这里整理了使用公告库、提交核实线索与识别冒名信息时最常遇到的问题。若这里没有覆盖你的情况，可以随时通过线索入口补充材料。"
          crumbs={[{ label: "首页", to: "/" }, { label: "常见问题" }]}
        />

        <section className="mx-auto grid max-w-content grid-cols-1 gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-12">
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950">按类别浏览</h2>
                <ul className="mt-4 space-y-1">
                  {categories.map((cat) => (
                    <li key={cat.id}>
                      <Link
                        to={`/statements?category=${cat.id}`}
                        className="flex items-center justify-between gap-3 rounded-md px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-50 hover:text-primary-700 cursor-pointer"
                      >
                        <span className="flex items-center gap-2">
                          <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                            <i className={cat.icon} />
                          </span>
                          {cat.name}
                        </span>
                        <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                          <i className="ri-arrow-right-s-line" />
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 rounded-lg border border-primary-200 bg-primary-50 p-6">
                <h2 className="font-heading text-base font-bold text-primary-800">仍没有答案？</h2>
                <p className="mt-2 text-sm leading-relaxed text-primary-900/80">
                  把遇到的问题连同原始链接一起提交，我们会在核实后补充说明，或直接回复你。
                </p>
                <Link
                  to="/contact"
                  className="mt-4 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  提交核实线索
                </Link>
                <p className="mt-4 break-all text-xs text-primary-900/70">{siteInfo.email}</p>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-8">
            <h2 className="sr-only">全部常见问题</h2>
            <FaqAccordion items={faqs} />
            <p className="mt-6 text-xs leading-relaxed text-foreground-500">
              以上说明仅为便于理解的信息梳理，具体以公告库中对应公告的原文与最新更新为准。
            </p>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}